import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BuilderSession } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Save,
  Upload,
  Loader2,
  Coins,
  AlertTriangle,
  LayoutDashboard,
  EllipsisVertical,
  Settings,
} from 'lucide-react';
import { ConfigPanel } from './ConfigPanel';

interface BuilderHeaderProps {
  session: BuilderSession | null;
  onSave: (name: string, description: string) => Promise<void>;
  onRequestReview: () => void;
  onReportProblem: () => void;
  onSettingsChange: (settings: unknown) => void;
  onNameChange?: (name: string) => void;
  isSaving: boolean;
  className?: string;
}

export function BuilderHeader({
  session,
  onSave,
  onRequestReview,
  onReportProblem,
  onSettingsChange,
  onNameChange,
  isSaving,
  className,
}: BuilderHeaderProps) {
  const navigate = useNavigate();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [name, setName] = useState(session?.currentName || '');
  const [description, setDescription] = useState(session?.currentDescription || '');

  const handleSave = async () => {
    if (!name.trim()) return;
    await onSave(name, description);
    setSaveDialogOpen(false);
  };

  const openSaveDialog = () => {
    setName(session?.currentName || '');
    setDescription(session?.currentDescription || '');
    setSaveDialogOpen(true);
  };

  const formatCost = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  return (
    <div className={cn('flex items-center justify-between border-b px-4 py-2', className)}>
      {/* Left: Back and title */}
      <div className="flex items-center gap-3 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={() => navigate('/a/arche_builder')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold truncate max-w-[150px] sm:max-w-none">
            {session?.currentName || 'Untitled Arche'}
          </h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{session?.status === 'active' ? 'Editing' : session?.status}</span>
            {session?.archId && (
              <>
                <span>-</span>
                <Badge variant="outline" className="text-xs h-5">
                  {session.archId}
                </Badge>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Center: Cost indicator — desktop */}
      <div className="hidden xl:flex items-center gap-2 text-sm text-muted-foreground">
        <Coins className="h-4 w-4" />
        <span>{formatCost(session?.totalCostCents || 0)} credits used</span>
      </div>

      {/* Mobile: Compact cost badge */}
      <div className="flex xl:hidden items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
        <Coins className="h-3.5 w-3.5" />
        <span>{formatCost(session?.totalCostCents || 0)}</span>
      </div>

      {/* Right: Actions — desktop */}
      <div className="hidden xl:flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/contributor/dashboard')}>
          <LayoutDashboard className="h-4 w-4 mr-2" />
          Dashboard
        </Button>

        <Button variant="ghost" size="sm" onClick={onReportProblem} disabled={!session}>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Report Problem
        </Button>

        <Button variant="ghost" size="sm" onClick={() => setConfigDialogOpen(true)}>
          <Settings className="h-4 w-4 mr-2" />
          Configure
        </Button>

        <Button variant="outline" size="sm" onClick={openSaveDialog} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Draft
        </Button>

        <Button size="sm" onClick={onRequestReview} disabled={!session?.archId}>
          <Upload className="h-4 w-4 mr-2" />
          Request Review
        </Button>
      </div>

      {/* Right: Actions — mobile dropdown */}
      <div className="xl:hidden flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <EllipsisVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate('/contributor/dashboard')}>
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onReportProblem} disabled={!session}>
              <AlertTriangle className="h-4 w-4 mr-2" />
              Report Problem
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setConfigDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configure
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openSaveDialog} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Draft
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onRequestReview} disabled={!session?.archId}>
              <Upload className="h-4 w-4 mr-2" />
              Request Review
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as Draft</DialogTitle>
            <DialogDescription>
              Save your Arche as a draft. You can continue editing it later.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome Arche"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                Description
              </label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this Arche do?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!name.trim() || isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configuration</DialogTitle>
            <DialogDescription>
              Configure your Arche settings including pricing, visibility, and theme.
            </DialogDescription>
          </DialogHeader>
          <ConfigPanel
            session={session}
            onSettingsChange={onSettingsChange}
            onNameChange={onNameChange}
            variant="dialog"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
